import MeetingProvider from './meeting/MeetingProvider';
import {
  MeetingConsumer,
  useMeeting
} from './meeting/meetingProviderContextDef';
import useParticipant from './participant/useParticipant';
import useStream from './stream/useStream';
import useConnection from './connection/useConnection';
import usePubSub from './pubSub/usePubSub';
import useRealtimeStore from './realtimeStore/useRealtimeStore';
import useFile from './upload/useFile';
import useMediaDevice from './useMediaDevice';
import useTranscription from './transcription/transcription';
import useTranslation from './translation/translation';
import useCharacter from './character/useCharacter';
import useWhiteboard from './whiteboard/useWhiteboard';
import {
  VideoSDK,
  CameraDeviceInfo,
  MicrophoneDeviceInfo,
  DeviceInfo,
  ExternalE2EEKeyProvider,
  PreCallTestError
} from '@videosdk.live/js-sdk';
import React, {
  useEffect,
  useRef,
  forwardRef,
  useMemo,
  useCallback
} from 'react';
import { debounce } from './utils';
import useAgentParticipant from './agent/useAgentParticipant';
import { useMeetingSelector } from './meeting/meetingProviderContextDef';

const Constants = VideoSDK.Constants;

/**
 * @param {{
 *  cameraId?: string | undefined,
 *  encoderConfig?: "h90p_w160p" | "h180p_w320p" | "h216p_w384p"
 *     | "h360p_w640p" | "h360p_w640p_150kbps" | "h540p_w960p" | "h720p_w1280p" | "h1080p_w1920p"
 *     | "h1440p_w2560p" | "h2160p_w3840p" | "h120p_w160p" | "h180p_w240p"
 *     | "h240p_w320p" | "h360p_w480p" | "h480p_w640p" | "h540p_w720p"
 *     | "h720p_w960p" | "h1080p_w1440p" | "h1440p_w1920p" | undefined,
 *  facingMode?: "user" | "environment" | undefined,
 *  optimizationMode?: "text"| "motion" | "detail" | undefined,
 *  multiStream?: boolean,
 *  bitrateMode?: "bandwidth_optimized" | "balanced" | "high_quality" | undefined,
 *  maxLayer?: 2 | 3 | undefined
 * }}
 */
const createCameraVideoTrack = async ({
  cameraId = undefined,
  encoderConfig = undefined,
  facingMode = undefined,
  optimizationMode = undefined,
  multiStream = true,
  bitrateMode = Constants.BitrateMode.BALANCED,
  maxLayer = 3
}) => {
  return await VideoSDK.createCameraVideoTrack({
    cameraId,
    encoderConfig,
    facingMode,
    optimizationMode,
    multiStream,
    bitrateMode,
    maxLayer
  });
};

/**
 * @param {'NONE' | 'ERROR' | 'WARN' | 'INFO' | 'DEBUG' | 'ALL'}
 */
const setLogLevel = (logLevel = Constants.LogLevel.INFO) => {
  VideoSDK.setLogLevel(logLevel);
};

/**
 * @param {{
 * token : string,
 * samplingDuration : number,
 * audioOnly : boolean,
 * audioTrack : MediaStream,
 * videoTrack : MediaStream,
 * videoConfig :{
 *  cameraId?: string | undefined,
 *  encoderConfig?: "h90p_w160p" | "h180p_w320p" | "h216p_w384p"
 *     | "h360p_w640p" | "h360p_w640p_150kbps" | "h540p_w960p" | "h720p_w1280p" | "h1080p_w1920p"
 *     | "h1440p_w2560p" | "h2160p_w3840p" | "h120p_w160p" | "h180p_w240p"
 *     | "h240p_w320p" | "h360p_w480p" | "h480p_w640p" | "h540p_w720p"
 *     | "h720p_w960p" | "h1080p_w1440p" | "h1440p_w1920p" | undefined,
 *  facingMode?: "user" | "environment" | undefined,
 *  optimizationMode?: "text"| "motion" | "detail" | undefined,
 *  multiStream?: boolean,
 *  bitrateMode?: "bandwidth_optimized" | "balanced" | "high_quality" | undefined,
 *  maxLayer?: 2 | 3 | undefined
 * },
 * audioConfig: {
 *  noiseConfig?: {
 *    echoCancellation: boolean,
 *    autoGainControl: boolean,
 *    noiseSuppression: boolean
 *  } | undefined,
 *  encoderConfig?: "speech_low_quality"
 *     | "speech_standard" | "music_standard"
 *     | "standard_stereo" | "high_quality"
 *     | "high_quality_stereo" | undefined
 *  microphoneId?: string | undefined
 * },
 * onStatsChange?: (stats: object) => void
 * }}
 */
const runPreCallTest = VideoSDK.runPreCallTest.bind(VideoSDK);

/**
 * @param {{
 *  encoderConfig?: "h360p_30fps" | "h480p_15fps" | "h480p_30fps"
 *       | "h720p_5fps" | "h720p_15fps"  | "h720p_30fps" | "h1080p_15fps" | "h1080p_30fps | undefined,
 *  optimizationMode?: "text"| "motion" | "detail" | undefined,
 *  withAudio?:"enable" | "disable"
 *  multiStream?: boolean
 * }}
 */
const createScreenShareVideoTrack = async ({
  encoderConfig = undefined,
  optimizationMode = undefined,
  withAudio = 'enable',
  multiStream = false
}) => {
  return await VideoSDK.createScreenShareVideoTrack({
    encoderConfig,
    optimizationMode,
    withAudio,
    multiStream
  });
};

/**
 * @param {{
 *  noiseConfig?: {
 *    echoCancellation: boolean,
 *    autoGainControl: boolean,
 *    noiseSuppression: boolean
 *  } | undefined,
 *  encoderConfig?: "speech_low_quality"
 *     | "speech_standard" | "music_standard"
 *     | "standard_stereo" | "high_quality"
 *     | "high_quality_stereo" | undefined
 *  microphoneId?: string | undefined
 * }}
 */
const createMicrophoneAudioTrack = async ({
  noiseConfig = undefined,
  encoderConfig = undefined,
  microphoneId = undefined
}) => {
  return await VideoSDK.createMicrophoneAudioTrack({
    noiseConfig,
    encoderConfig,
    microphoneId
  });
};

/**
 * @deprecated
 * @param {{
 * timeoutDuration?: number | undefined,
 * baseUrl?: string | undefined,
 * }}
 * @returns {Promise<{downloadSpeed: number,uploadSpeed: number}> }
 */
const getNetworkStats = async ({ timeoutDuration, baseUrl } = {}) => {
  return await VideoSDK.getNetworkStats({ timeoutDuration, baseUrl });
};

/**
 * Wraps a video player component with IntersectionObserver and ResizeObserver
 * to pause/resume video streams and update viewport dimensions based on visibility and size.
 *
 * @param VideoPlayerComponent - The React component to wrap.
 *
 * @returns A new React functional component with adaptive logic.
 */
const withAdaptiveObservers = VideoPlayerComponent => {
  const intersectionObserverOptions = {
    root: null,
    rootMargin: '0px',
    threshold: 0
  };
  const debounceDelay = 400;

  return props => {
    const { participantId, type } = props;
    const ref = useRef(null);
    const observerRef = useRef(null);
    const resizeObserverRef = useRef(null);
    const { participant } = useParticipant(participantId);
    const selectLocalParticipant = useCallback(s => s.localParticipant, []);
    const localParticipant = useMeetingSelector(selectLocalParticipant);
    const participantRef = useRef(participant);
    participantRef.current = participant;

    useEffect(() => {
      // Skip observer setup for "share" type and local participant
      if (
        type === 'share' ||
        !ref.current ||
        !participant ||
        participantId === localParticipant?.id
      )
        return;

      const element = ref.current;
      if (!element) return;

      // Disconnect previous observer before creating a new one
      if (observerRef.current) observerRef.current.disconnect();

      const handleIntersectionChange = debounce(entries => {
        if (!Array.isArray(entries) || entries.length === 0) return;
        const entry = entries[entries.length - 1];
        const currentParticipant = participantRef.current;
        if (!currentParticipant) return;

        if (entry.intersectionRatio > 0) {
          for (const stream of currentParticipant.streams.values()) {
            if (stream.kind === 'video' && stream.pausedBy !== 'SUB_MANAGER') {
              stream.resume();
            }
          }
        } else {
          for (const stream of currentParticipant.streams.values()) {
            if (stream.kind === 'video') {
              stream.pause();
            }
          }
        }
      }, debounceDelay);

      observerRef.current = new IntersectionObserver(
        handleIntersectionChange,
        intersectionObserverOptions
      );

      const handleResize = debounce(entries => {
        const {
          contentRect: { width, height }
        } = entries[0];
        if (width > 0 && height > 0) {
          // Read from ref — always latest participant
          participantRef.current?.setViewPort(width, height);
        }
      }, debounceDelay);

      resizeObserverRef.current = new ResizeObserver(handleResize);
      resizeObserverRef.current.observe(element);
      observerRef.current.observe(element);

      return () => {
        observerRef.current?.disconnect();
        resizeObserverRef.current?.disconnect();
      };
    }, []);

    return <VideoPlayerComponent ref={ref} {...props} />;
  };
};

/**
 * Renders a video player for a participant. Handles attaching media stream
 * to video element and mirroring for local participant.
 *
 * @param participantId - ID of the participant.(Mandatory)
 * @param type - Type of stream (e.g., 'video' or 'share').
 * @param containerStyle - Optional CSS style for the outer container.
 * @param className - Optional CSS class for the container.
 * @param classNameVideo - Optional CSS class for the video element.
 * @param videoStyle - Optional CSS style for the video element.
 * @param videoRef - Optional ref to the underlying <video> element.
 */

const VideoSDKPlayer = forwardRef(
  (
    {
      participantId,
      type = 'video',
      containerStyle = {},
      className = '',
      classNameVideo = '',
      videoStyle = {},
      videoRef
    },
    ref
  ) => {
    const {
      webcamOn,
      webcamStream,
      isLocal,
      screenShareStream,
      screenShareOn
    } = useParticipant(participantId);
    const internalRef = useRef(null);
    const finalVideoRef = videoRef || internalRef;

    useEffect(() => {
      const stream = type === 'share' ? screenShareStream : webcamStream;
      const isOn = type === 'share' ? screenShareOn : webcamOn;
      if (finalVideoRef.current) {
        if (isOn && stream) {
          const mediaStream = new MediaStream();
          mediaStream.addTrack(stream.track);
          finalVideoRef.current.setAttribute('muted', '');
          finalVideoRef.current.srcObject = mediaStream;
          finalVideoRef.current.setAttribute('playsinline', '');
          finalVideoRef.current.setAttribute('x5-playsinline', '');
          finalVideoRef.current.setAttribute('webkit-playsinline', '');
          finalVideoRef.current
            .play()
            .catch(error =>
              console.error('finalVideoRef.current.play() failed', error)
            );
        } else {
          finalVideoRef.current.srcObject = null;
        }
      }
    }, [
      type,
      ...(type === 'share'
        ? [screenShareOn, screenShareStream]
        : type === 'video'
        ? [webcamOn, webcamStream]
        : [])
    ]);

    return (
      <div
        ref={ref}
        className={`video-container participant-video-${participantId} ${className ||
          ''}`}
        style={{
          objectFit: type === 'share' ? 'contain' : 'none',
          height: '100%',
          ...containerStyle
        }}
      >
        <video
          width="100%"
          height="100%"
          ref={finalVideoRef}
          autoPlay
          className={classNameVideo}
          muted
          style={
            isLocal && type !== 'share'
              ? {
                  transform: 'scaleX(-1)',
                  WebkitTransform: 'scaleX(-1)',
                  ...videoStyle
                }
              : videoStyle
          }
        />
      </div>
    );
  }
);

/**
 * Renders a video player for a participant. Handles attaching media stream
 * to video element and mirroring for local participant.
 *
 * @param participantId - ID of the participant.(Mandatory)
 * @param type - Type of stream (e.g., 'video' or 'share').
 * @param containerStyle - Optional CSS style for the outer container.
 * @param className - Optional CSS class for the container.
 * @param classNameVideo - Optional CSS class for the video element.
 * @param videoStyle - Optional CSS style for the video element.
 * @param videoRef - Optional ref to the underlying <video> element.
 */

const VideoPlayer = ({ participantId, type, ...props }) => {
  // Memoize the wrapped component to prevent unnecessary re-renders
  const VideoPlayerComponent = useMemo(() => {
    return type === 'share'
      ? VideoSDKPlayer
      : withAdaptiveObservers(VideoSDKPlayer);
  }, [type, participantId]);

  return (
    <VideoPlayerComponent
      participantId={participantId}
      type={type}
      {...props}
    />
  );
};

/**
 * Renders an audio player for a participant's mic or screen share audio.
 *
 * @param participantId - ID of the participant.(Mandatory)
 * @param type - Type of audio stream ('mic' or 'shareAudio').
 * @returns A HTML audio element with the stream attached.
 */

const AudioPlayer = ({ participantId, type }) => {
  // Getting participant properties
  const { micStream, isLocal, screenShareAudioStream } = useParticipant(
    participantId
  );

  const micRef = useRef(null);

  useEffect(() => {
    if (micRef.current) {
      const audioStream =
        type === 'shareAudio' ? screenShareAudioStream : micStream;

      if (audioStream) {
        const mediaStream = new MediaStream();
        mediaStream.addTrack(audioStream.track);

        micRef.current.srcObject = mediaStream;
        micRef.current
          .play()
          .catch(error => console.error('micRef.current.play() failed', error));
      } else {
        micRef.current.srcObject = null;
      }
    }
  }, [micStream, screenShareAudioStream, type]);

  return <audio ref={micRef} autoPlay playsInline muted={isLocal} />;
};

export {
  VideoPlayer,
  AudioPlayer,
  withAdaptiveObservers,
  MeetingProvider,
  useMeeting,
  useTranscription,
  useTranslation,
  useWhiteboard,
  useCharacter,
  useParticipant,
  useAgentParticipant,
  useStream,
  usePubSub,
  useRealtimeStore,
  useFile,
  MeetingConsumer,
  useConnection,
  Constants,
  createCameraVideoTrack,
  setLogLevel,
  runPreCallTest,
  PreCallTestError,
  createMicrophoneAudioTrack,
  createScreenShareVideoTrack,
  useMediaDevice,
  getNetworkStats,
  CameraDeviceInfo,
  MicrophoneDeviceInfo,
  DeviceInfo,
  ExternalE2EEKeyProvider
};
